/**
 * Zoom sterowany scrollem: warstwa startuje powiększona (maxScale) i schodzi do minScale,
 * gdy góra kadru zbliża się do góry viewportu (transform: scale, origin center).
 *
 * Wymagania: kontener `overflow: hidden` (np. `rounded-*` + crop).
 *
 * Przykład — tło (CSS background):
 * <div class="relative overflow-hidden" data-scroll-zoom data-scroll-zoom-max="1.18">
 *   <div data-scroll-zoom-layer class="absolute inset-0 bg-cover" style="background-image:url(...)"></div>
 * </div>
 *
 * Przykład — <img>:
 * <div class="relative overflow-hidden" data-scroll-zoom data-scroll-zoom-max="1.12">
 *   <img data-scroll-zoom-layer class="absolute inset-0 size-full object-cover" src="..." alt="">
 * </div>
 *
 * Opcjonalnie: data-scroll-zoom-min="1" (domyślnie 1).
 *
 * Tryb `exit-grow` (np. hero pod sticky headerem): `data-scroll-zoom-mode="exit-grow"` —
 * skala trzyma min, dopóki kadr nie „wyjedzie” w górę (scroll w dół); wtedy rośnie do max.
 * Opcjonalnie: `data-scroll-zoom-exit-range="0.45"` — ułamek wysokości viewportu do pełnego max.
 */
const FRAME_SELECTOR = '[data-scroll-zoom]';
const LAYER_SELECTOR = '[data-scroll-zoom-layer]';

const DEFAULT_EXIT_RANGE_VH = 0.45;

function parsePositiveRatio(value, fallback) {
    const n = parseFloat(String(value), 10);
    return Number.isFinite(n) && n > 0 ? n : fallback;
}

function smoothStepZoom(t) {
    const x = Math.min(Math.max(t, 0), 1);
    return x * x * (3 - 2 * x);
}

function parseScale(value, fallback) {
    const n = parseFloat(String(value), 10);
    return Number.isFinite(n) && n > 0 ? n : fallback;
}

export function initScrollZoomOnScroll() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    const frames = document.querySelectorAll(FRAME_SELECTOR);
    if (!frames.length) {
        return;
    }

    const instances = [];

    frames.forEach(frame => {
        const layer = frame.querySelector(LAYER_SELECTOR);

        if (!layer) {
            return;
        }

        const maxScale = parseScale(frame.dataset.scrollZoomMax, 1.3);
        const minScale = parseScale(frame.dataset.scrollZoomMin, 1);
        const mode = frame.dataset.scrollZoomMode || 'default';
        const exitRangeVh = parsePositiveRatio(frame.dataset.scrollZoomExitRange, DEFAULT_EXIT_RANGE_VH);

        instances.push({
            frame,
            layer,
            maxScale: Math.max(maxScale, minScale),
            minScale,
            mode,
            exitRangeVh,
        });
    });

    if (!instances.length) {
        return;
    }

    let ticking = false;

    function update() {
        ticking = false;
        const vh = window.innerHeight || 1;

        instances.forEach(({ frame, layer, maxScale, minScale, mode, exitRangeVh }) => {
            const rect = frame.getBoundingClientRect();

            if (rect.bottom < -80 || rect.top > vh + 80) {
                layer.style.transform = '';
                layer.style.willChange = 'auto';
                return;
            }

            let scale;

            if (mode === 'exit-grow') {
                scale = minScale;
                if (rect.top < 0) {
                    const u = -rect.top / (vh * exitRangeVh);
                    const k = smoothStepZoom(Math.min(Math.max(u, 0), 1));
                    scale = minScale + (maxScale - minScale) * k;
                }
            } else {
                const t = Math.min(Math.max(rect.top / vh, 0), 1);
                const progress = 1 - t;
                scale = maxScale - (maxScale - minScale) * progress;
            }

            layer.style.transformOrigin = '50% 50%';
            layer.style.transform = `scale3d(${scale.toFixed(4)}, ${scale.toFixed(4)}, 1)`;
            layer.style.willChange = 'transform';
        });
    }

    function scheduleUpdate() {
        if (!ticking) {
            ticking = true;
            requestAnimationFrame(update);
        }
    }

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    scheduleUpdate();
}
